import { useState, useEffect, useMemo } from 'react';
import type { MockNCR, MockCAPA } from '@/mocks/qms';
import { getAllOperators } from '@/api/operators.api';
import { useToast } from '@/contexts/ToastContext';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

interface Props {
  ncr: MockNCR | null;
  onClose: () => void;
  onCreated: (capa: MockCAPA) => void;
}

interface OperatorState {
  id: string;
  name: string;
  employee_code: string;
  skill: string;
}

const rootCauseCategories = [
  { value: 'MATERIAL' as const, label: 'Material', color: 'bg-orange-100 text-orange-700' },
  { value: 'MACHINE' as const, label: 'Machine', color: 'bg-gray-100 text-gray-700' },
  { value: 'METHOD' as const, label: 'Method', color: 'bg-sky-100 text-sky-700' },
  { value: 'MANPOWER' as const, label: 'Manpower', color: 'bg-amber-100 text-amber-700' },
  { value: 'MEASUREMENT' as const, label: 'Measurement', color: 'bg-violet-100 text-violet-700' },
  { value: 'ENVIRONMENT' as const, label: 'Environment', color: 'bg-emerald-100 text-emerald-700' },
];

export default function CAPAFormSlideOver({ ncr, onClose, onCreated }: Props) {
  const toast = useToast();
  const [type, setType] = useState<MockCAPA['type']>('CORRECTIVE');
  const [rootCauseCategory, setRootCauseCategory] = useState<MockCAPA['rootCauseCategory']>('MATERIAL');
  const [rootCause, setRootCause] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [preventiveAction, setPreventiveAction] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const [operators, setOperators] = useState<OperatorState[]>([]);

  useEffect(() => {
    let active = true;
    async function loadData() {
      try {
        const res = await getAllOperators();
        if (active && res.success && res.data) {
          setOperators(res.data);
        }
      } catch (err) {
        console.error('Failed to load operators in CAPAFormSlideOver:', err);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const operatorOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select operator --' },
    ...operators.map((o) => ({
      value: o.id,
      label: o.name,
      sublabel: o.skill ? o.skill.replace(/_/g, ' ') : undefined
    }))
  ], [operators]);

  const save = () => {
    if (!ncr) {
      toast.error('No NCR linked');
      return;
    }
    if (!rootCause.trim()) {
      toast.error('Root cause description is required');
      return;
    }
    if (!correctiveAction.trim()) {
      toast.error('Corrective action is required');
      return;
    }
    if (!preventiveAction.trim()) {
      toast.error('Preventive action is required');
      return;
    }
    if (!assignedTo) {
      toast.error('Assignee is required');
      return;
    }
    if (!targetDate) {
      toast.error('Target date is required');
      return;
    }

    const assignedOp = operators.find((o) => o.id === assignedTo);
    const suffix = Date.now().toString().slice(-4);

    const newCAPA: MockCAPA = {
      id: `capa-${suffix}`,
      capaNumber: `CAPA-${new Date().getFullYear()}-${suffix}`,
      ncrId: ncr.id,
      ncrNumber: ncr.ncrNumber,
      type,
      rootCause: rootCause.trim(),
      rootCauseCategory,
      correctiveAction: correctiveAction.trim(),
      preventiveAction: preventiveAction.trim(),
      targetDate,
      status: 'OPEN',
      assignedTo,
      assignedToName: assignedOp?.name || '',
      verifiedBy: null,
      verifiedDate: null,
      effectiveness: null,
      notes: null,
      createdAt: new Date().toISOString(),
    };

    mockCAPAs.push(newCAPA);
    onCreated(newCAPA);
    toast.success(`CAPA ${newCAPA.capaNumber} created`);
    onClose();
  };

  if (!ncr) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">CAPA — {ncr.ncrNumber}</h2>
            <p className="text-sm text-gray-500">Corrective and Preventive Action</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line text-gray-500 text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* NCR Summary */}
          <div className="bg-violet-50 rounded-lg p-4 border border-violet-100">
            <p className="text-xs font-medium text-violet-700 uppercase tracking-wide mb-2">Linked NCR</p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Item</span>
                <span className="font-medium text-gray-900">{ncr.itemName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Defect</span>
                <span className="text-gray-700">{ncr.defectDescription}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Severity</span>
                <span className="font-medium text-red-600">{ncr.severity}</span>
              </div>
            </div>
          </div>

          {/* Type Toggle */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">CAPA Type</label>
            <div className="flex gap-2">
              {(['CORRECTIVE', 'PREVENTIVE'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border whitespace-nowrap cursor-pointer ${
                    type === t
                      ? t === 'CORRECTIVE'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t === 'CORRECTIVE' ? 'Corrective (Fix Current)' : 'Preventive (Prevent Recurrence)'}
                </button>
              ))}
            </div>
          </div>

          {/* Root Cause Category */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Root Cause Category (5M+E) <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {rootCauseCategories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setRootCauseCategory(cat.value)}
                  className={`px-2 py-2 rounded-md text-xs font-medium border text-center whitespace-nowrap cursor-pointer ${
                    rootCauseCategory === cat.value
                      ? `${cat.color} border-current`
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Root Cause Description */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Root Cause Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              placeholder="What caused this non-conformance?"
            />
          </div>

          {/* Corrective Action */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Corrective Action <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">— What will be done to fix current issue?</span>
            </label>
            <textarea
              value={correctiveAction}
              onChange={(e) => setCorrectiveAction(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              placeholder="Describe the immediate fix..."
            />
          </div>

          {/* Preventive Action */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Preventive Action <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">— What will be done to prevent recurrence?</span>
            </label>
            <textarea
              value={preventiveAction}
              onChange={(e) => setPreventiveAction(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              placeholder="Describe the long-term prevention..."
            />
          </div>

          {/* Assigned To */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Assigned To <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={assignedTo}
              onChange={(val) => setAssignedTo(val)}
              options={operatorOptions}
              placeholder="-- Select operator --"
              searchPlaceholder="Search operator..."
            />
          </div>

          {/* Target Date */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Target Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={save}
            className="px-4 py-2 bg-violet-600 text-white rounded-md text-sm font-medium hover:bg-violet-700 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shield-check-line mr-1" />
            Save CAPA
          </button>
        </div>
      </div>
    </div>
  );
}